import { Button, Flex, Spacing, Tooltip } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { FecImportConfirmationKind } from '../../../../enums/fecImportConfirmationKind'

interface FecImportConfirmationActionsProps {
  canDelete: boolean
  canRetryAnalysis: boolean
  confirmationKind: FecImportConfirmationKind
  isReuploading: boolean
  isRetryingAnalysis: boolean
  onRetryAnalysis?: () => void
  onReupload: () => void
  onReviewMappings: () => void
}

export const FecImportConfirmationActions = ({
  canDelete,
  canRetryAnalysis,
  confirmationKind,
  isReuploading,
  isRetryingAnalysis,
  onRetryAnalysis,
  onReupload,
  onReviewMappings,
}: FecImportConfirmationActionsProps): ReactElement => {
  const { t } = useTranslation()
  const isTransientError = confirmationKind === 'network' || confirmationKind === 'timeout'

  // The backend no longer deletes this import, so the button that starts over stays off. Say why.
  const withLockedTooltip = (button: ReactElement): ReactElement =>
    canDelete ? (
      button
    ) : (
      <Tooltip
        label={t('settings.organization.accounting.fec_file_import.modal.confirmation_step.reupload_locked_tooltip')}
        placement="top"
      >
        <Tooltip.DisabledWrapper>{button}</Tooltip.DisabledWrapper>
      </Tooltip>
    )

  if (isTransientError) {
    const showUploadNewFile = confirmationKind === 'network' || !canRetryAnalysis

    return (
      <Flex gap={Spacing.S10} justifyContent="flex-end">
        {showUploadNewFile &&
          withLockedTooltip(
            <Button
              disabled={isReuploading || !canDelete}
              loading={isReuploading}
              onClick={onReupload}
              variant={canRetryAnalysis ? 'tonal' : 'filled'}
            >
              {t('settings.organization.accounting.fec_file_import.modal.confirmation_step.upload_new_file_button')}
            </Button>,
          )}
        {canRetryAnalysis && (
          <Button disabled={isRetryingAnalysis} loading={isRetryingAnalysis} onClick={onRetryAnalysis} variant="filled">
            {t('settings.organization.accounting.fec_file_import.modal.confirmation_step.retry_analysis_button')}
          </Button>
        )}
      </Flex>
    )
  }

  return (
    <Flex gap={Spacing.S10} justifyContent="flex-end">
      {withLockedTooltip(
        <Button
          disabled={isReuploading || !canDelete}
          loading={isReuploading}
          onClick={onReupload}
          variant={confirmationKind === 'partial' ? 'tonal' : 'outlined'}
        >
          {t('settings.organization.accounting.fec_file_import.modal.confirmation_step.reupload_button')}
        </Button>,
      )}
      {confirmationKind === 'all-failed' && canRetryAnalysis && (
        <Button disabled={isRetryingAnalysis} loading={isRetryingAnalysis} onClick={onRetryAnalysis} variant="filled">
          {t('settings.organization.accounting.fec_file_import.modal.confirmation_step.retry_analysis_button')}
        </Button>
      )}
      {confirmationKind !== 'all-failed' && (
        <Button iconRight="arrowRight" onClick={onReviewMappings} variant="filled">
          {t('settings.organization.accounting.fec_file_import.modal.confirmation_step.review_mappings_button')}
        </Button>
      )}
    </Flex>
  )
}
